import * as React from 'react';
import { connect } from 'react-redux';
import { assign as _assign, includes as _includes } from 'lodash';
import { GlobalPrivateState } from 'src/api';
import { RequestApiResult, requestAPIActions } from 'loopback-react-request';
import { ConfigurationModel, PlateSurfaceDefaultModel, PlateSurfaceModel, COLORS } from 'franke-trough-dependencies';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';

import { FormattedMessage } from 'react-intl';
import MESSAGES from './Surface.messages';
import { isConfigurationEditable } from 'src/redux/utils.redux';

export interface ColourProps {
  configuration: RequestApiResult<ConfigurationModel>;
  dispatch: any;
}

export interface ColourState {
  colour?: COLORS;
  dropdownOpen: boolean;
}

const ColorLabel: React.SFC<{value: COLORS}> = (props) => {
  switch (props.value) {
    case 'white':
      return (
        <span>
          <FormattedMessage {...MESSAGES.colorLabelAlpineWhite} />
          <br />
          <small><FormattedMessage {...MESSAGES.colorLabelAlpineWhiteText} /></small>
        </span>
      );
    case 'black':
      return (
        <span>
          <FormattedMessage {...MESSAGES.colorLabelGraniteBlack} />
          <br />
          <small><FormattedMessage {...MESSAGES.colorLabelGraniteBlackText} /></small>
        </span>
      );
    case 'black-white':
      return (
        <span>
          <FormattedMessage {...MESSAGES.colorLabelGraniteBlackWhite} />
          <br />
          <small><FormattedMessage {...MESSAGES.colorLabelGraniteBlackWhiteText} /></small>
        </span>
      );
    case 'light-gray':
      return (
        <span>
          <FormattedMessage {...MESSAGES.colorLabelGraniteLightGray} />
          <br />
          <small><FormattedMessage {...MESSAGES.colorLabelGraniteLightGrayText} /></small>
        </span>
      );
    default:
      return null;
  }  
};

export class ColourPartial extends React.Component<ColourProps, ColourState> {
  constructor(props: ColourProps) {
    super(props);
    this.state = {
      dropdownOpen: false,
    };
    this.handleClick = this.handleClick.bind(this);
    this.toggle = this.toggle.bind(this);
  }
  
  componentDidMount() {
    if (!this.colour) {
      return;
    }

    this.setState({
      colour: this.colour,
    });
  }

  componentDidUpdate(prevProps: ColourProps) {
    const {
      configuration,
    } = this.props;

    const prevConfiguration = prevProps.configuration;

    if (
      (configuration.result && prevConfiguration.result) && 
      (configuration.result.updated_at !== prevConfiguration.result.updated_at)  
    ) {
      if (!this.colour) { return; }
      this.setState({
        colour: this.colour,
      });
    }
  }

  public render() {
    const selected = this.state.colour || this.colour || (this.surfaceDefaults && this.surfaceDefaults.color.default) || 'white';
    return (
      <div className="partial-colour pb-4">
        <Dropdown
          className="dropdown-select"
          disabled={this.disabled}
          isOpen={this.state.dropdownOpen} 
          toggle={this.toggle}
        >
          <DropdownToggle 
            caret={true} 
            disabled={this.disabled}
          >
            <ColorLabel value={selected} />
          </DropdownToggle>
          <DropdownMenu>
            {this.surfaceDefaults && this.surfaceDefaults.color && this.surfaceDefaults.color.allValues && this.surfaceDefaults.color.allValues.map((value, index) => {
              const key = `surface_color_key_${index}`;
              const disabled = !(_includes(this.surfaceDefaults && this.surfaceDefaults.color.values, value));

              return (
                <DropdownItem 
                  key={key} 
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => this.handleClick(event, value)}
                  disabled={value === selected || disabled}
                >
                  <ColorLabel value={value} />
                </DropdownItem>
              );
            })}
          </DropdownMenu>
        </Dropdown>
      </div>
    );
  }

  private get colour(): PlateSurfaceModel['color'] | null | undefined {
    return this.surface && this.surface.color;
  }

  private get disabled(): boolean {
    return !isConfigurationEditable();
  }

  private dispatchConfiguration(colour?: COLORS) {
    const {
      configuration
    } = this.props;
    
    if (!configuration || !configuration.result) { return; }

    if (!colour) { return; }
    const surface: PlateSurfaceModel = this.surface as PlateSurfaceModel;
    const data: PlateSurfaceModel = _assign({}, surface, {
      color: colour,
    });
    this.props.dispatch(requestAPIActions.load(
      'configuration', {
        url: `/Configurations/${configuration.result && configuration.result.id}`,
        options: {
          method: 'PATCH',
          data: {surface: data}
        },
      },
      true,
    ));
  }

  private handleClick(event: React.MouseEvent<HTMLButtonElement>, value: COLORS): void {
    this.setState({
      colour: value,
      dropdownOpen: false,
    });
    this.dispatchConfiguration(value);
  }

  private get surface(): PlateSurfaceModel| null | undefined {
    const {
      configuration,
    } = this.props;
    return configuration &&
      configuration.result &&
      configuration.result.surface;
  }

  private get surfaceDefaults(): PlateSurfaceDefaultModel | null | undefined {
    const {
      configuration,
    } = this.props;
    return configuration &&
      configuration.result && 
      configuration.result.defaults &&
      configuration.result.defaults.surface;
  }

  private toggle() {
    this.setState(prevState => ({
      dropdownOpen: !prevState.dropdownOpen
    }));
  }

}

export const Colour = connect((state: GlobalPrivateState) => ({
  configuration: state.configuration, 
}))(ColourPartial);

export default Colour;
